import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PendingIcon from "@mui/icons-material/Pending";
import qicardPayments from "../../../services/qicardPayments";
import { emitPaymentsChanged } from "../../../services/paymentsEvents";
import {
  clearAbandonOnPaymentSuccess,
  markOrderPaymentAbandoned,
} from "../../../utils/abandonedOrderPayments";
import {
  cancelGatewayPayment,
  handlePaymentClose,
  releaseCheckoutGatewayPayment,
} from "../../../utils/paymentCloseCleanup";
import { formatCheckoutIqd } from "../../../utils/formatCheckoutIqd";
import { useCart } from "../../../contexts/CartContext";
import { emitCheckoutUiEvent } from "../../../utils/checkoutEvents";
import {
  clearHeldCheckoutOrder,
  loadHeldCheckoutOrder,
} from "../../../utils/checkoutSession";
import { showToast } from "../../component/common/toastNotification";
import {
  QiCardPaymentKind,
  QiCardPendingContext,
} from "../../../types/qicard";
import {
  clearQiCardPending,
  loadQiCardPending,
  resolveTransactionIdFromUrl,
  saveQiCardPending,
} from "../../../utils/qicardStorage";
import {
  startQiCardOrderPayment,
  startQiCardWalletRecharge,
} from "../../../utils/qicardPayFlow";
import { NJIM } from "../../../theme/njimTheme";
import {
  isQiCardIncompleteCheckoutStatus,
  isQiCardTerminalStatus,
  mapQiStatusToUi,
  QiCardUiState,
} from "../../../utils/qicardStatus";


const POLL_FAST_MS = 2000;
const POLL_SLOW_MS = 5000;
const POLL_FAST_DURATION_MS = 20000;
const POLL_MAX_MS = 90000;
const INCOMPLETE_RETURN_MS = 8000;
const MAX_CONSECUTIVE_POLL_ERRORS = 3;

type PageState = QiCardUiState | "unknown" | "canceling";

const QiCardReturnPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [searchParams] = useSearchParams();
  const [context, setContext] = useState<QiCardPendingContext | null>(null);
  const [pageState, setPageState] = useState<PageState>("pending");
  const [gatewayStatus, setGatewayStatus] = useState("");
  const [pollError, setPollError] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const pollStartedAt = useRef<number>(Date.now());
  const pollTimerRef = useRef<number | null>(null);
  const consecutiveErrorsRef = useRef(0);
  const paymentsEmittedRef = useRef(false);

  useEffect(() => {
    const urlId = resolveTransactionIdFromUrl(searchParams);
    const stored = loadQiCardPending();

    if (urlId) {
      const merged: QiCardPendingContext = {
        transactionId: urlId,
        kind: stored?.kind ?? "order",
        orderId: stored?.orderId,
        amount: stored?.amount,
        startedAt: stored?.startedAt ?? Date.now(),
      };
      saveQiCardPending(merged);
      setContext(merged);
      return;
    }

    if (stored) {
      setContext(stored);
      return;
    }

    setPageState("unknown");
  }, [searchParams]);

  const announceOrderOutcome = useCallback(
    (kind: QiCardUiState | "timeout") => {
      if (context?.kind === "wallet") return;
      const held = loadHeldCheckoutOrder();
      const isCheckout = Boolean(
        held?.orderId && context?.orderId && held.orderId === context.orderId
      );
      if (kind === "success") {
        if (isCheckout) {
          clearCart();
          clearHeldCheckoutOrder();
        }
        emitCheckoutUiEvent({
          type: "success",
          amount: context?.amount ?? 0,
          orderId: context?.orderId,
          landOn: "step1",
        });
        return;
      }
      if (kind === "cancelled") {
        if (isCheckout) emitCheckoutUiEvent({ type: "keep_review" });
        showToast(
          t("salesOrder.noPaymentTaken", {
            defaultValue: "No payment was taken.",
          }),
          "info"
        );
        return;
      }
      if (kind === "failed") {
        if (isCheckout) emitCheckoutUiEvent({ type: "keep_review" });
        return;
      }
      if (kind === "timeout") {
        if (isCheckout) emitCheckoutUiEvent({ type: "keep_review" });
        showToast(
          t("salesOrder.unresolvedPayment", {
            defaultValue:
              "We haven’t had confirmation yet. Check Invoices & Payments shortly.",
          }),
          "info"
        );
      }
    },
    [clearCart, context?.amount, context?.kind, context?.orderId, t]
  );

  const releaseQiCardWithoutAbandoningOrder = useCallback(async () => {
    if (!context?.transactionId) return;
    const held = loadHeldCheckoutOrder();
    const isCheckoutDraft = Boolean(
      held?.orderId && context.orderId && held.orderId === context.orderId
    );
    if (isCheckoutDraft) {
      await releaseCheckoutGatewayPayment({
        type: "QiCard",
        transactionId: context.transactionId,
      });
      return;
    }
    if (context.orderId) {
      await handlePaymentClose({
        orderId: context.orderId,
        type: "QiCard",
        transactionId: context.transactionId,
      });
    }
  }, [context?.orderId, context?.transactionId]);

  const applyTerminalState = useCallback(
    async (status: string) => {
      const ui = mapQiStatusToUi(status);
      if (ui === "success") {
        if (!paymentsEmittedRef.current) {
          paymentsEmittedRef.current = true;
          emitPaymentsChanged();
        }
        clearAbandonOnPaymentSuccess(context?.orderId, context?.transactionId);
        clearQiCardPending();
        announceOrderOutcome("success");
        setPageState("success");
        return;
      }
      if (ui === "cancelled") {
        await releaseQiCardWithoutAbandoningOrder();
        clearQiCardPending();
        announceOrderOutcome("cancelled");
        setPageState("cancelled");
        return;
      }
      if (ui === "failed") {
        const held = loadHeldCheckoutOrder();
        const isCheckoutDraft = Boolean(
          held?.orderId && context?.orderId && held.orderId === context.orderId
        );
        if (context?.orderId && !isCheckoutDraft) {
          markOrderPaymentAbandoned(context.orderId);
          emitPaymentsChanged();
        }
        clearQiCardPending();
        announceOrderOutcome("failed");
        setPageState("failed");
        return;
      }
    },
    [announceOrderOutcome, context?.orderId, context?.transactionId, releaseQiCardWithoutAbandoningOrder]
  );

  const pollStatus = useCallback(async () => {
    if (!context?.transactionId) return;

    try {
      const { status } = await qicardPayments.getStatus(context.transactionId);
      consecutiveErrorsRef.current = 0;
      setPollError(false);
      setGatewayStatus(status);

      if (isQiCardTerminalStatus(status)) {
        await applyTerminalState(status);
        return true;
      }

      const elapsed = Date.now() - pollStartedAt.current;
      if (
        isQiCardIncompleteCheckoutStatus(status) &&
        elapsed >= INCOMPLETE_RETURN_MS
      ) {
        if (context.orderId) {
          await releaseQiCardWithoutAbandoningOrder();
        }
        clearQiCardPending();
        announceOrderOutcome("cancelled");
        setPageState("cancelled");
        return true;
      }

      if (elapsed >= POLL_MAX_MS) {
        announceOrderOutcome("timeout");
        setPageState("timeout");
        return true;
      }

      return false;
    } catch (err) {
      const axiosErr = err as { response?: { status?: number } };
      if (axiosErr.response?.status === 404) {
        setPollError(true);
        setPageState("unknown");
        return true;
      }

      consecutiveErrorsRef.current += 1;
      if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_POLL_ERRORS) {
        setPollError(true);
      }
      return false;
    }
  }, [announceOrderOutcome, applyTerminalState, context?.orderId, context?.transactionId, releaseQiCardWithoutAbandoningOrder]);

  useEffect(() => {
    if (!context?.transactionId) return;
    if (
      pageState === "success" ||
      pageState === "failed" ||
      pageState === "cancelled" ||
      pageState === "timeout" ||
      pageState === "unknown" ||
      pageState === "canceling"
    ) {
      return;
    }

    pollStartedAt.current = Date.now();

    const scheduleNext = (delayMs: number) => {
      pollTimerRef.current = window.setTimeout(runPoll, delayMs);
    };

    const runPoll = async () => {
      const done = await pollStatus();
      if (done) return;

      const elapsed = Date.now() - pollStartedAt.current;
      const delay =
        elapsed < POLL_FAST_DURATION_MS ? POLL_FAST_MS : POLL_SLOW_MS;
      scheduleNext(delay);
    };

    void runPoll();

    return () => {
      if (pollTimerRef.current != null) {
        window.clearTimeout(pollTimerRef.current);
      }
    };
  }, [context?.transactionId, pageState, pollStatus]);

  const finishAsCancelled = useCallback(async () => {
    await releaseQiCardWithoutAbandoningOrder();
    clearQiCardPending();
    announceOrderOutcome("cancelled");
    setPageState("cancelled");
  }, [announceOrderOutcome, releaseQiCardWithoutAbandoningOrder]);

  const handleCancelPayment = async () => {
    if (!context?.transactionId) return;
    const confirmed = window.confirm(t("qicard.cancelConfirm"));
    if (!confirmed) return;

    setPageState("canceling");
    const outcome = await cancelGatewayPayment(context.transactionId);

    if (outcome === "completed") {
      const { status } = await qicardPayments.getStatus(context.transactionId);
      setGatewayStatus(status);
      await applyTerminalState(status || "SUCCESS");
      return;
    }

    if (outcome === "still_processing") {
      setPageState("pending");
      showToast(
        t("salesOrder.paymentStillWithProvider", {
          defaultValue:
            "The payment provider is still processing this payment. Please wait for it to finish.",
        }),
        "info"
      );
      return;
    }

    if (outcome === "failed") {
      setPageState("pending");
      showToast(
        t("salesOrder.paymentCancelFailed", {
          defaultValue: "Could not cancel this payment. Please try again.",
        }),
        "error"
      );
      return;
    }

    await finishAsCancelled();
  };

  const handlePayAgain = async () => {
    if (!context || actionLoading) return;
    setActionLoading(true);
    try {
      if (context.kind === "wallet" && context.amount) {
        await startQiCardWalletRecharge({
          amount: context.amount,
          language: i18n.language,
        });
      } else if (context.orderId && context.amount != null) {
        await startQiCardOrderPayment({
          orderId: context.orderId,
          amount: context.amount,
          language: i18n.language,
        });
      }
    } catch {
      setActionLoading(false);
    }
  };

  const kind: QiCardPaymentKind = context?.kind ?? "order";
  const isWallet = kind === "wallet";

  const titleKey = (() => {
    switch (pageState) {
      case "success":
        return isWallet ? "qicard.walletSuccessTitle" : "qicard.orderSuccessTitle";
      case "failed":
        return isWallet ? "qicard.walletFailedTitle" : "qicard.orderFailedTitle";
      case "cancelled":
        return isWallet ? "qicard.walletCancelledTitle" : "qicard.orderCancelledTitle";
      case "timeout":
        return "qicard.timeoutTitle";
      case "unknown":
        return "qicard.unknownTitle";
      case "canceling":
        return "qicard.cancelingTitle";
      default:
        return "qicard.pendingTitle";
    }
  })();

  const messageKey = (() => {
    switch (pageState) {
      case "success":
        return isWallet ? "qicard.walletSuccessMessage" : "qicard.orderSuccessMessage";
      case "failed":
        return isWallet ? "qicard.walletFailedMessage" : "qicard.orderFailedMessage";
      case "cancelled":
        return isWallet ? "qicard.walletCancelledMessage" : "qicard.orderCancelledMessage";
      case "timeout":
        return isWallet ? "qicard.walletTimeoutMessage" : "qicard.orderTimeoutMessage";
      case "unknown":
        return "qicard.unknownMessage";
      case "canceling":
        return "qicard.cancelingMessage";
      default:
        return isWallet ? "qicard.walletPendingMessage" : "qicard.orderPendingMessage";
    }
  })();

  const icon = (() => {
    if (pageState === "success") {
      return <CheckCircleIcon sx={{ fontSize: 56, color: NJIM.mint }} />;
    }
    if (pageState === "failed" || pageState === "unknown") {
      return <CancelIcon sx={{ fontSize: 56, color: NJIM.danger }} />;
    }
    if (pageState === "cancelled") {
      return <CancelIcon sx={{ fontSize: 56, color: NJIM.textSecondary }} />;
    }
    return <PendingIcon sx={{ fontSize: 56, color: NJIM.mint }} />;
  })();

  const showSpinner =
    pageState === "pending" || pageState === "canceling";

  const primaryLink = isWallet
    ? "/walletBalance"
    : pageState === "cancelled" || pageState === "failed"
      ? "/saleOrder?checkout=1"
      : "/saleOrder?tab=OrderHistory";
  const primaryLinkLabel = isWallet
    ? t("qicard.goToWallet")
    : pageState === "cancelled" || pageState === "failed"
      ? t("salesOrder.continueCheckout", { defaultValue: "Continue checkout" })
      : t("qicard.goToOrders");

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: NJIM.muted,
        p: { xs: 2, sm: 3 },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 520,
          bgcolor: NJIM.white,
          borderRadius: "16px",
          border: `1px solid ${NJIM.border}`,
          boxShadow: NJIM.elevation,
          p: { xs: 3, sm: 4 },
          textAlign: "center",
        }}
      >
        <Box sx={{ mb: 2, position: "relative", minHeight: 56 }}>
          {icon}
          {showSpinner && (
            <CircularProgress
              size={72}
              thickness={2}
              sx={{
                color: NJIM.mint,
                position: "absolute",
                top: -8,
                left: "50%",
                ml: "-36px",
              }}
            />
          )}
        </Box>

        <Typography
          sx={{
            fontSize: { xs: 20, sm: 22 },
            fontWeight: 600,
            color: NJIM.textPrimary,
            mb: 1,
          }}
        >
          {t(titleKey)}
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: 14, sm: 15 },
            color: NJIM.textSecondary,
            mb: 2.5,
            lineHeight: 1.5,
          }}
        >
          {t(messageKey)}
        </Typography>

        {context?.amount != null && pageState === "success" && (
          <Typography
            sx={{
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              fontSize: 18,
              fontWeight: 600,
              color: NJIM.mintForeground,
              mb: 2,
            }}
          >
            {formatCheckoutIqd(context.amount)}
          </Typography>
        )}

        {pollError && pageState === "pending" && (
          <Typography sx={{ fontSize: 13, color: NJIM.danger, mb: 2 }}>
            {t("qicard.pollErrorHint")}
          </Typography>
        )}

        {gatewayStatus && pageState === "pending" && (
          <Typography sx={{ fontSize: 12, color: NJIM.textSecondary, mb: 2 }}>
            {t("qicard.statusLabel")}: {gatewayStatus}
          </Typography>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {pageState === "success" && (
            <Button
              component={RouterLink}
              to={primaryLink}
              variant="contained"
              fullWidth
              sx={{ borderRadius: "999px", textTransform: "none", fontWeight: 600 }}
            >
              {primaryLinkLabel}
            </Button>
          )}

          {pageState === "cancelled" && !isWallet && (
            <Button
              variant="contained"
              fullWidth
              onClick={() => navigate(`/saleOrder?checkout=${Date.now()}`)}
              sx={{ borderRadius: "999px", textTransform: "none", fontWeight: 600 }}
            >
              {t("salesOrder.continueCheckout", { defaultValue: "Continue checkout" })}
            </Button>
          )}

          {((pageState === "failed" && context) || (pageState === "cancelled" && isWallet && context)) && (
            <Button
              variant="contained"
              fullWidth
              disabled={actionLoading}
              onClick={handlePayAgain}
              sx={{ borderRadius: "999px", textTransform: "none", fontWeight: 600 }}
            >
              {actionLoading ? (
                <CircularProgress size={22} sx={{ color: NJIM.white }} />
              ) : pageState === "cancelled" ? (
                t("qicard.payAgain")
              ) : (
                t("qicard.tryAgain")
              )}
            </Button>
          )}

          {pageState === "timeout" && (
            <Button
              component={RouterLink}
              to={primaryLink}
              variant="contained"
              fullWidth
              sx={{ borderRadius: "999px", textTransform: "none", fontWeight: 600 }}
            >
              {primaryLinkLabel}
            </Button>
          )}

          {(pageState === "pending" || pageState === "timeout") && context && (
            <Button
              variant="outlined"
              fullWidth
              onClick={handleCancelPayment}
              sx={{
                borderRadius: "999px",
                textTransform: "none",
                fontWeight: 600,
                borderColor: NJIM.border,
                color: NJIM.textPrimary,
              }}
            >
              {t("qicard.cancelPayment")}
            </Button>
          )}

          {pageState === "unknown" && (
            <Button
              component={RouterLink}
              to="/saleOrder?tab=OrderHistory"
              variant="contained"
              fullWidth
              sx={{ borderRadius: "999px", textTransform: "none", fontWeight: 600 }}
            >
              {t("qicard.goToOrders")}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default QiCardReturnPage;
